import React, { useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { useAtom } from "jotai"
import { toast } from "react-toastify"
import ProfileTabs from "../components/ProfileTabs"
import ProfileTitle from "../components/ProfileTitle"
import Pagination from "../components/commons/Pagination"
import { userAtom } from "../store"
import { getBlockedSellers, unblockSeller } from "../firebase/blocks"
import { formatDate } from "../utils"

/**
 * The buyer's block list, and the only way back off it.
 *
 * A blocked seller is hidden from the marketplace and their storefront says
 * only that it is blocked, so without this page an accidental block would be
 * hard to find again and impossible to undo.
 */
const BlockedSellersPage = () => {
    const [userData] = useAtom(userAtom)
    const [sellers, setSellers] = useState([])
    const [loading, setLoading] = useState(true)
    const [unblocking, setUnblocking] = useState(null)
    const [currentPage, setCurrentPage] = useState(1)
    const itemsPerPage = 6

    const fields = ["Seller", "Blocked On", ""]

    useEffect(() => {
        if (!userData?.id) return

        getBlockedSellers(userData.id).then((data) => {
            setSellers(data)
            setLoading(false)
        })
    }, [userData?.id])

    const handleUnblock = async (seller) => {
        setUnblocking(seller.id)
        const ok = await unblockSeller(userData.id, seller.id)
        setUnblocking(null)

        if (!ok) {
            toast.error("Could not unblock this seller. Please try again.")
            return
        }

        setSellers((previous) => previous.filter((entry) => entry.id !== seller.id))
        toast.success(`${seller.displayName || "Seller"} is unblocked.`)
    }

    // `blocked_at` is a Firestore timestamp on entries written since the
    // field existed; anything older simply has no date to show.
    const blockedOn = (seller) => (
        seller.blocked_at?.seconds ? formatDate(seller.blocked_at.seconds) : '—'
    )

    const visible = sellers.slice(itemsPerPage * (currentPage - 1), itemsPerPage * currentPage)

    return (
        <div>
            <ProfileTitle />

            <ProfileTabs activeTab="Blocked Sellers" />

            <p className="text-[#717171] text-sm mb-8">
                Blocked sellers are hidden from the marketplace, along with every Shopify store they run.
                You can unblock any of them here.
            </p>

            {loading ? (
                <div className="text-white text-center mt-8">Loading...</div>
            ) : sellers.length === 0 ? (
                <div className="text-white text-lg text-center mt-8">
                    You have not blocked any sellers
                </div>
            ) : (
                <>
                    <div className="max-lg:hidden">
                        <div className="w-full overflow-auto">
                            <table className="w-full">
                                <thead>
                                    <tr className="bg-[#2E2E2E] border-[1px] border-gray py-3 text-sm text-[#717171]">
                                        {fields.map((item, index) => (
                                            <th className={`py-3 text-left ${index > 0 ? '' : 'pl-3'}`} key={`blocked-field-${index}`}>
                                                {item}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>

                                <div className="pt-1" />
                                <tbody className="text-white space-y-1 text-sm">
                                    {visible.map((seller) => (
                                        <tr key={`blocked-${seller.id}`} className="border-[1px] border-gray text-nowrap">
                                            <td className="py-2 pl-3 pr-20 lg:min-w-[400px] min-w-[200px]">
                                                <div className="flex items-center">
                                                    <img
                                                        src={seller.picture || "/assets/avatars/default.png"}
                                                        alt="seller"
                                                        className="w-[50px] h-[50px] mr-6 border-[1px] border-gray object-cover"
                                                    />

                                                    <span className="text-sm">
                                                        {seller.displayName || seller.id}
                                                    </span>
                                                </div>
                                            </td>

                                            <td className="lg:min-w-[200px] pr-10">
                                                {blockedOn(seller)}
                                            </td>

                                            <td className="pr-3">
                                                <div className="flex items-center justify-end gap-4">
                                                    <Link
                                                        className="text-xs text-primary underline"
                                                        to={`/user-profile/${seller.id}`}
                                                    >
                                                        View Profile
                                                    </Link>

                                                    <button
                                                        className={`solo-button flex items-center justify-center text-white border-white border-[1px] bg-primary w-[140px] h-[45px] ${unblocking === seller.id ? 'opacity-50' : ''}`}
                                                        onClick={() => handleUnblock(seller)}
                                                        disabled={unblocking === seller.id}
                                                    >
                                                        {unblocking === seller.id ? 'Unblocking...' : 'Unblock'}
                                                    </button>
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        <div className="mt-9">
                            <Pagination
                                itemsPerPage={itemsPerPage}
                                totalItems={sellers.length}
                                onPageChange={(value) => setCurrentPage(value)}
                            />
                        </div>
                    </div>

                    <div className="lg:hidden grid sm:grid-cols-2 grid-cols-1 gap-5">
                        {sellers.map((seller) => (
                            <div className="p-[10px] border-[1px] border-gray bg-[#191919]" key={`blocked-mobile-${seller.id}`}>
                                <div className="flex w-full items-center">
                                    <img
                                        src={seller.picture || "/assets/avatars/default.png"}
                                        className="w-[60px] h-[60px] object-cover border-[1px] border-gray mr-4"
                                        alt="seller"
                                    />

                                    <div className="w-full">
                                        <div className="text-white text-sm">
                                            {seller.displayName || seller.id}
                                        </div>

                                        <div className="mt-2 w-full flex justify-between text-[#717171] text-xs">
                                            <span>Blocked On</span>
                                            <span className="text-white">{blockedOn(seller)}</span>
                                        </div>
                                    </div>
                                </div>

                                <button
                                    className={`flex items-center justify-center w-full mt-3 text-sm text-white border-gray border-[1px] h-[40px] ${unblocking === seller.id ? 'opacity-50' : ''}`}
                                    onClick={() => handleUnblock(seller)}
                                    disabled={unblocking === seller.id}
                                >
                                    {unblocking === seller.id ? 'Unblocking...' : 'Unblock'}
                                </button>
                            </div>
                        ))}
                    </div>
                </>
            )}
        </div>
    )
}

export default BlockedSellersPage
